import { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery } from "convex/react";
import { anyApi } from "convex/server";

// Bridges Clerk auth state and the Convex `users` row. On first
// sign-in, fires `users.upsertCurrent` to create the row, and resolves
// to "ready" once the row is queryable. Other queries that depend on
// `getCurrentUserOrThrow` should wait for state === "ready".
//
// State machine:
//   anonymous  Clerk says not signed in. Caller can show landing.
//   loading    Clerk loaded + signed in, Convex query in flight.
//   syncing    Convex returned null (no row yet); upsert mutation is
//              in flight to create it.
//   ready      Row exists. Other Convex queries can run safely.
export type CurrentUserState =
  | { state: "anonymous" }
  | { state: "loading" }
  | { state: "syncing" }
  | { state: "ready"; user: ConvexUser };

export interface ConvexUser {
  _id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  clerkUserId: string;
}

export function useEnsuredCurrentUser(): CurrentUserState {
  const { isLoaded, isSignedIn } = useUser();
  const skip = !isLoaded || !isSignedIn;
  const me = useQuery(anyApi.users.getCurrent, skip ? "skip" : {}) as
    | ConvexUser
    | null
    | undefined;
  const upsert = useMutation(anyApi.users.upsertCurrent);

  useEffect(() => {
    if (!skip && me === null) {
      upsert({}).catch((err) => {
        // UI stays in "syncing" until a future refetch resolves it.
        // Most likely cause is a transient auth-token race at first
        // load — we'll retry on next mount / focus.
        console.error("[maisons] users.upsertCurrent failed", err);
      });
    }
  }, [skip, me, upsert]);

  if (skip) return { state: "anonymous" };
  if (me === undefined) return { state: "loading" };
  if (me === null) return { state: "syncing" };
  return { state: "ready", user: me };
}
